import { formatPrice } from '../lib/price';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { getLevelLabel as sharedLevelLabel, getLevelBadgeClass } from '../utils/level';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  Users, 
  CalendarBlank, 
  Clock,
  Plus,
  Medal,
  CreditCard,
  Check,
  X,
  ShareNetwork,
  WhatsappLogo,
  CopySimple,
  LockKey,
  LockKeyOpen
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import MemberBadge from '../components/MemberBadge';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { CompetitiveResultForm } from '../components/CompetitiveResultForm';

const OpenMatches = () => {
  const { user, t, language, API } = useAuth();
  const dateLocale = language === 'de' ? de : enUS;

  const [matches, setMatches] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [resultModal, setResultModal] = useState(null);  // currently selected match for result entry
  const [resultWinners, setResultWinners] = useState([]); // ids of selected winners (max 2)
  const [compResult, setCompResult] = useState({ valid: false });
  const [submittingResult, setSubmittingResult] = useState(false);
  const [showPast, setShowPast] = useState(false); // admin-only: include past matches
  // Admin: add a specific customer to a match.
  const [adminAddModal, setAdminAddModal] = useState(null); // match object
  const [adminAddQuery, setAdminAddQuery] = useState('');
  const [adminAddResults, setAdminAddResults] = useState([]);
  const [adminAddChosen, setAdminAddChosen] = useState(null);
  const [adminAdding, setAdminAdding] = useState(false);

  // Smart level suggestion: ±1 around the user's level, clamped to 1.0–7.0
  const suggestedLevels = () => {
    const lvl = Number(user?.player_level || 3.0);
    const min = Math.max(1.0, Math.round((lvl - 1.0) * 2) / 2);
    const max = Math.min(7.0, Math.round((lvl + 1.0) * 2) / 2);
    return { min, max };
  };

  const defaultMatch = () => {
    const s = suggestedLevels();
    return {
      date: new Date(),
      start_time: '',           // filled once user picks a date+duration
      duration_minutes: 90,
      match_type: 'doubles',
      match_kind: 'social',     // 'social' | 'competitive' (competitive feeds Padlr Rating)
      max_players: 4,
      min_level: s.min,
      max_level: s.max,
      notes: ''
    };
  };

  const [newMatch, setNewMatch] = useState(defaultMatch());
  const [availableTimes, setAvailableTimes] = useState([]);   // [{start_time, available_courts}]
  const [loadingTimes, setLoadingTimes] = useState(false);

  const openCreateModal = () => {
    setNewMatch(defaultMatch());
    setAvailableTimes([]);
    setShowCreateModal(true);
  };

  // Fetch available start times whenever date or duration changes in the modal
  useEffect(() => {
    if (!showCreateModal) return;
    const fetchTimes = async () => {
      try {
        setLoadingTimes(true);
        const dateStr = format(newMatch.date, 'yyyy-MM-dd');
        const { data } = await API.get(`/matches/available-times?date=${dateStr}&duration=${newMatch.duration_minutes}`);
        setAvailableTimes(data.times || []);
        // Auto-pick a friendly default if current start_time is no longer valid
        const times = data.times || [];
        const stillValid = times.some(t => t.start_time === newMatch.start_time);
        if (!stillValid) {
          // Prefer 18:00 if available, else first of the day
          const preferred = times.find(t => t.start_time === '18:00') || times[0];
          setNewMatch(prev => ({ ...prev, start_time: preferred ? preferred.start_time : '' }));
        }
      } catch (e) {
        setAvailableTimes([]);
      } finally {
        setLoadingTimes(false);
      }
    };
    fetchTimes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreateModal, newMatch.date, newMatch.duration_minutes]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPast]);

  // Debounced user search for the admin "add player" modal.
  useEffect(() => {
    if (!adminAddModal) return;
    if (!adminAddQuery || adminAddQuery.length < 2) { setAdminAddResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get(`/users?q=${encodeURIComponent(adminAddQuery)}&limit=8`);
        setAdminAddResults(data?.users || []);
      } catch {
        setAdminAddResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAddQuery, adminAddModal]);

  const submitAdminAddPlayer = async () => {
    if (!adminAddModal?.id || !adminAddChosen?.id) return;
    setAdminAdding(true);
    try {
      await API.post(`/admin/matches/${adminAddModal.id}/add-player`, {
        user_id: adminAddChosen.id,
        payment_method: 'club',
        skip_level_check: true,
      });
      toast.success(language === 'de' ? 'Spieler hinzugefügt' : 'Player added');
      setAdminAddModal(null);
      setAdminAddChosen(null);
      setAdminAddQuery('');
      setAdminAddResults([]);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'de' ? 'Fehlgeschlagen' : 'Failed'));
    } finally {
      setAdminAdding(false);
    }
  };


  const loadData = async () => {
    try {
      setLoading(true);
      const isAdmin = user?.role === 'admin' || user?.role === 'staff';
      const pastQs = isAdmin && showPast ? '&include_past=true' : '';
      const [matchesRes, myMatchesRes, courtsRes] = await Promise.all([
        API.get(`/matches?_=1${pastQs}`),
        API.get(`/matches?my_matches=true${pastQs}`),
        API.get('/courts')
      ]);
      setMatches(matchesRes.data);
      setMyMatches(myMatchesRes.data.filter(m => m.status !== 'cancelled'));
      setCourts(courtsRes.data);
    } catch (error) {
      console.error('Error loading matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const [pendingJoinMatch, setPendingJoinMatch] = useState(null); // match obj when payment dialog open
  const [createPaymentMethod, setCreatePaymentMethod] = useState('club'); // 'club' | 'online'

  const handleJoinMatch = (match) => {
    // Open the small payment-mode dialog. Actual API call happens after they pick.
    setPendingJoinMatch(match);
  };

  const confirmJoinMatch = async (paymentMethod) => {
    if (!pendingJoinMatch) return;
    const match = pendingJoinMatch;
    setPendingJoinMatch(null);
    try {
      await API.post(`/matches/${match.id}/join`, { payment_method: paymentMethod });
      if (paymentMethod === 'online') {
        // Kick off Datatrans checkout for cost_per_player
        try {
          const origin = window.location.origin;
          const { data } = await API.post('/payments/checkout', {
            match_id: match.id,
            origin_url: origin,
          });
          if (data?.url) {
            window.location.href = data.url;
            return;
          }
        } catch (perr) {
          toast.error(language === 'de'
            ? 'Beitritt erfolgreich, Zahlung konnte nicht gestartet werden — bitte später im Profil zahlen'
            : 'Joined OK, but couldn\'t start payment — please pay from your profile later');
        }
      }
      toast.success(language === 'de' ? 'Erfolgreich beigetreten!' : 'Successfully joined!');
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'de' ? 'Beitreten fehlgeschlagen' : 'Failed to join');
      toast.error(msg);
    }
  };

  const handleLeaveMatch = async (matchId) => {
    // Warn the user client-side when they're inside the 8 h window and a
    // court is already reserved — the server enforces it too but a friendlier
    // confirm dialog avoids a surprise error toast.
    const match = matches.find(m => m.id === matchId) || {};
    const isCreator = match.created_by_id === user.id;
    try {
      const now = new Date();
      const start = new Date(`${match.date}T${match.start_time}:00`);
      const hoursUntil = (start.getTime() - now.getTime()) / 3.6e6;
      const insideWindow = match.court_reserved && hoursUntil < 8;
      if (insideWindow) {
        const msg = language === 'de'
          ? 'Weniger als 8 h vor Spielbeginn — du kannst nicht mehr online austreten. Bitte danach den Club kontaktieren um deinen Anteil zu bezahlen.'
          : 'Less than 8h before start — you can no longer leave online. Please contact the club afterwards to settle your share.';
        toast.error(msg);
        return;
      }
    } catch (_e) { /* fall through, server will decide */ }

    // Creators: warn about ownership transfer before confirming.
    if (isCreator) {
      const otherPlayers = (match.current_players || []).filter(p => p.id !== user.id);
      const nextOwner = otherPlayers[0];
      const nextName = nextOwner?.name || (language === 'de' ? 'der nächste Spieler' : 'the next player');
      const confirmMsg = language === 'de'
        ? `Match verlassen? ${nextName} wird automatisch zum neuen Organisator.`
        : `Leave match? ${nextName} will automatically become the new organizer.`;
      if (!window.confirm(confirmMsg)) return;
    }

    try {
      const { data } = await API.post(`/matches/${matchId}/leave`);
      const transferred = !!data?.ownership_transferred_to;
      toast.success(
        transferred
          ? (language === 'de' ? 'Verlassen — neuer Organisator übernommen' : 'Left — new organizer taken over')
          : (language === 'de' ? 'Erfolgreich verlassen' : 'Successfully left'),
      );
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'de' ? 'Verlassen fehlgeschlagen' : 'Failed to leave');
      toast.error(msg);
    }
  };

  const handleCancelMatch = async (match) => {
    // Ask for a confirmation, and warn if inside the 8 h window when a court
    // is already reserved (server enforces this too, but a clearer UX helps).
    const now = new Date();
    const start = new Date(`${match.date}T${match.start_time}:00`);
    const hoursUntil = (start.getTime() - now.getTime()) / 3.6e6;
    const insideWindow = match.court_reserved && hoursUntil < 8;
    const msg = insideWindow
      ? (language === 'de'
          ? 'Weniger als 8 h vor Spielbeginn — nur der Club kann jetzt absagen. Bitte danach den Club kontaktieren um den Court zu bezahlen.'
          : 'Less than 8h before start — only the club can cancel now. Please contact the club afterwards to settle the court fee.')
      : (language === 'de'
          ? 'Match wirklich absagen? Reservierter Platz wird freigegeben.'
          : 'Cancel this match? Reserved court will be released.');
    if (insideWindow) {
      toast.error(msg);
      return;
    }
    if (!window.confirm(msg)) return;
    try {
      await API.delete(`/matches/${match.id}`);
      toast.success(language === 'de' ? 'Match abgesagt' : 'Match cancelled');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'de' ? 'Absage fehlgeschlagen' : 'Cancel failed'));
    }
  };

  const handleCreateMatch = async () => {
    try {
      const dateStr = format(newMatch.date, 'yyyy-MM-dd');
      if (!newMatch.start_time) {
        toast.error(language === 'de' ? 'Bitte eine Zeit wählen' : 'Please pick a time');
        return;
      }
      // Padelarena is doubles-only — always 4 players. Backend auto-picks the court.
      const { data } = await API.post('/matches', {
        date: dateStr,
        start_time: newMatch.start_time,
        duration_minutes: newMatch.duration_minutes,
        match_type: 'doubles',
        match_kind: newMatch.match_kind || 'social',
        max_players: 4,
        min_level: newMatch.min_level,
        max_level: newMatch.max_level,
        notes: newMatch.notes,
        payment_method: createPaymentMethod,
      });
      toast.success(language === 'de' ? 'Spiel erstellt!' : 'Match created!');
      setShowCreateModal(false);
      // If they chose online, kick off checkout right away for the creator
      if (createPaymentMethod === 'online' && data?.id) {
        try {
          const origin = window.location.origin;
          const resp = await API.post('/payments/checkout', { match_id: data.id, origin_url: origin });
          if (resp.data?.url) {
            window.location.href = resp.data.url;
            return;
          }
        } catch {
          toast.error(language === 'de'
            ? 'Spiel erstellt, Zahlung konnte nicht gestartet werden — später bezahlen'
            : 'Match created, but couldn\'t start payment — pay later');
        }
      }
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'de' ? 'Erstellung fehlgeschlagen' : 'Failed to create');
      toast.error(msg);
    }
  };

  const isJoined = (match) => match.current_players?.some(p => p.id === user.id);

  const toggleWinner = (pid) => {
    setResultWinners(prev => {
      if (prev.includes(pid)) return prev.filter(id => id !== pid);
      if (prev.length >= 2) return prev;
      return [...prev, pid];
    });
  };

  const handleSubmitResult = async () => {
    if (!resultModal) return;
    const isComp = resultModal.match_kind === 'competitive';
    if (isComp ? !compResult.valid : resultWinners.length !== 2) {
      toast.error(language === 'de' ? (isComp ? 'Bitte Teams und Resultat eingeben' : 'Bitte 2 Sieger wählen') : (isComp ? 'Enter teams and score' : 'Pick 2 winners'));
      return;
    }
    setSubmittingResult(true);
    try {
      const res = await API.post(`/matches/${resultModal.id}/result`, isComp
        ? { winner_ids: compResult.winner_ids, sets: compResult.sets }
        : { winner_ids: resultWinners });
      // Backend returns `friendly: true` for social matches — tailor the
      // toast so we don't claim a rating update happened when it didn't.
      const wasFriendly = res?.data?.friendly === true || resultModal.match_kind !== 'competitive';
      toast.success(
        wasFriendly
          ? (language === 'de' ? 'Freundschaftsspiel gespeichert (kein Rating-Update)' : 'Friendly result saved (no rating change)')
          : (language === 'de' ? 'Resultat erfasst — Padlr Rating aktualisiert' : 'Result saved — Padlr Rating updated'),
      );
      setResultModal(null);
      setResultWinners([]);
      // refresh
      const { data: all } = await API.get('/matches');
      setMatches(all);
      const { data: mine } = await API.get('/matches?my_matches=true');
      setMyMatches(mine.filter(m => m.status !== 'cancelled'));
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === 'de' ? 'Fehler beim Speichern' : 'Save failed'));
    } finally {
      setSubmittingResult(false);
    }
  };

  const buildShareMessage = (match) => {
    const dateStr = format(new Date(match.date), 'EEE dd.MM', { locale: dateLocale });
    const lvl = `${match.min_level}–${match.max_level}`;
    const spotsLeft = match.max_players - match.current_players.length;
    const link = `${window.location.origin}/match/${match.id}`;
    if (language === 'de') {
      return `🎾 Spiele mit mir bei Padelarena Wädenswil!\n` +
        `${match.court_name} · ${dateStr} ${match.start_time} · Level ${lvl}\n` +
        `Noch ${spotsLeft} ${spotsLeft === 1 ? 'Platz' : 'Plätze'} frei → ${link}`;
    }
    return `🎾 Play with me at Padelarena Wädenswil!\n` +
      `${match.court_name} · ${dateStr} ${match.start_time} · Level ${lvl}\n` +
      `${spotsLeft} ${spotsLeft === 1 ? 'spot' : 'spots'} left → ${link}`;
  };

  const handleWhatsAppShare = (match) => {
    const url = `https://wa.me/?text=${encodeURIComponent(buildShareMessage(match))}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleCopyLink = async (match) => {
    const link = `${window.location.origin}/match/${match.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(language === 'de' ? 'Link kopiert' : 'Link copied');
    } catch {
      toast.error(language === 'de' ? 'Kopieren fehlgeschlagen' : 'Copy failed');
    }
  };

  const getLevelBadge = (level) => getLevelBadgeClass(level);

  const getLevelLabel = (level) => sharedLevelLabel(level, language);

  const renderMatchCard = (match) => {
    const joined = isJoined(match);
    const spotsLeft = match.max_players - match.current_players.length;
    const isFull = spotsLeft === 0;
    const isCreator = match.created_by_id === user.id;
    const courtReserved = !!match.court_reserved;

    return (
      <motion.div
        key={match.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="match-card"
        data-testid={`match-card-${match.id}`}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-900 text-lg truncate">{match.court_name}</h3>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-slate-500 text-sm">{language === 'de' ? 'Doppel' : 'Doubles'}</p>
              {match.match_kind === 'competitive' ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-amber-500 text-white shadow-sm" data-testid={`match-kind-badge-${match.id}`}>
                  🏆 {language === 'de' ? 'Wertungsmatch · Rating' : 'Rated · Padlr Rating'}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-sky-100 text-sky-700 border border-sky-200" data-testid={`match-kind-badge-${match.id}`}>
                  🎾 {language === 'de' ? 'Freundschaftsspiel' : 'Friendly · No Rating'}
                </span>
              )}
            </div>
          </div>
          <span className={`flex-shrink-0 px-2 py-1 text-xs font-bold uppercase rounded-lg ${
            isFull 
              ? 'bg-red-50 text-red-500' 
              : 'bg-green-50 text-green-600'
          }`}>
            {isFull ? t('matchFull') : `${spotsLeft} ${t('spotsLeft')}`}
          </span>
        </div>

        {/* Court reservation status */}
        <div className={`mb-4 px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-2 border ${
          courtReserved 
            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
            : 'bg-amber-50 text-amber-700 border-amber-100'
        }`} data-testid={`court-status-${match.id}`}>
          {courtReserved 
            ? <><LockKey size={14} weight="fill" /> {language === 'de' ? 'Platz reserviert' : 'Court reserved'}</>
            : <><LockKeyOpen size={14} /> {language === 'de' ? 'Wartet auf 2. Spieler — Platz noch nicht gesperrt' : 'Awaiting 2nd player — court not yet locked'}</>}
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3 text-slate-500">
            <CalendarBlank size={16} weight="duotone" />
            <span data-testid={`match-date-${match.id}`}>{format(new Date(match.date), 'EEEE, PPP', { locale: dateLocale })}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-500">
            <Clock size={16} weight="duotone" />
            <span>{match.start_time} - {match.end_time}</span>
          </div>
          <div className="flex items-center gap-3 text-slate-500">
            <Medal size={16} weight="duotone" />
            <span>{t('level')}: {match.min_level} - {match.max_level}</span>
          </div>
        </div>

        {/* Players */}
        <div className="mb-4">
          <p className="label-text mb-2">{t('players')} ({match.current_players.length}/{match.max_players})</p>
          <div className="flex flex-wrap gap-2">
            {match.current_players.map((player, idx) => (
              <div 
                key={idx}
                className={`px-3 py-1 rounded-lg text-sm flex items-center gap-1.5 ${
                  player.id === user.id 
                    ? 'bg-emerald-500/20 text-emerald-600 border border-emerald-500/30'
                    : 'bg-white/10 text-slate-900'
                }`}
              >
                <span>{player.name}</span>
                <MemberBadge user={player} size={13} title={language === 'de' ? 'Verifiziertes Mitglied' : 'Verified member'} />
                {player.level != null && <span className="text-slate-500">({player.level})</span>}
              </div>
            ))}
            {Array.from({ length: spotsLeft }).map((_, idx) => (
              <div key={`empty-${idx}`} className="px-3 py-1 rounded-lg text-sm border border-dashed border-slate-200 text-slate-500">
                {language === 'de' ? 'Offen' : 'Open'}
              </div>
            ))}
          </div>
        </div>

        {match.notes && (
          <p className="text-slate-500 text-sm mb-4 italic">"{match.notes}"</p>
        )}

        {/* Price & Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <div>
            <p className="text-xs text-slate-500">{language === 'de' ? 'Pro Spieler' : 'Per player'}</p>
            <p className="text-xl font-bold text-emerald-600">{formatPrice(match.cost_per_player)} CHF</p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Share buttons */}
            {!isFull && (
              <>
                <button
                  type="button"
                  onClick={() => handleWhatsAppShare(match)}
                  title={language === 'de' ? 'Auf WhatsApp teilen' : 'Share on WhatsApp'}
                  className="h-9 w-9 flex items-center justify-center rounded-lg bg-[#25D366] text-white hover:bg-[#1ebc59] transition-colors"
                  data-testid={`whatsapp-share-${match.id}`}
                >
                  <WhatsappLogo size={18} weight="fill" />
                </button>
                <button
                  type="button"
                  onClick={() => handleCopyLink(match)}
                  title={language === 'de' ? 'Link kopieren' : 'Copy link'}
                  className="h-9 w-9 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                  data-testid={`copy-link-${match.id}`}
                >
                  <CopySimple size={16} />
                </button>
              </>
            )}

            {isCreator ? (
              <>
                {/* Creator can leave (transferring ownership) IF ≥1 other
                    player is on the match. Otherwise only "Cancel" makes
                    sense — nobody would be left to own it. */}
                {match.current_players.length > 1 && !match.from_booking && (
                  <Button
                    onClick={() => handleLeaveMatch(match.id)}
                    className="btn-secondary flex items-center gap-2"
                    data-testid={`leave-match-${match.id}`}
                    title={language === 'de'
                      ? 'Match verlassen — der nächste Spieler wird automatisch Organisator'
                      : 'Leave match — the next player automatically becomes organizer'}
                  >
                    <X size={16} />
                    {t('leaveMatch')}
                  </Button>
                )}
                <Button
                  onClick={() => handleCancelMatch(match)}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 flex items-center gap-2"
                  data-testid={`cancel-match-${match.id}`}
                >
                  <X size={16} />
                  {language === 'de' ? 'Match absagen' : 'Cancel match'}
                </Button>
              </>
            ) : joined ? (
              <Button 
                onClick={() => handleLeaveMatch(match.id)}
                className="btn-secondary flex items-center gap-2"
                data-testid={`leave-match-${match.id}`}
              >
                <X size={16} />
                {t('leaveMatch')}
              </Button>
            ) : (
              <Button 
                onClick={() => handleJoinMatch(match)}
                disabled={isFull || user.player_level < match.min_level || user.player_level > match.max_level}
                className="btn-primary flex items-center gap-2"
                data-testid={`join-match-${match.id}`}
              >
                <Check size={16} />
                {t('joinMatch')}
              </Button>
            )}
            {isFull && !match.result_recorded && (joined || isCreator || user.role === 'admin' || user.role === 'staff') && (
              <Button
                onClick={() => setResultModal(match)}
                className="bg-amber-500 hover:bg-amber-600 text-white font-bold flex items-center gap-2"
                data-testid={`submit-result-${match.id}`}
              >
                🏆 {match.match_kind === 'competitive' ? (language === 'de' ? 'Resultat eingeben' : 'Enter score') : (language === 'de' ? 'Sieger eingeben' : 'Submit result')}
              </Button>
            )}
            {match.result_recorded && (
              <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1.5 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid={`result-recorded-badge-${match.id}`}>
                ✓ {language === 'de' ? 'Resultat erfasst' : 'Result recorded'}{match.score_text ? ` · ${match.score_text}` : ''}
              </span>
            )}
            {(user?.role === 'admin' || user?.role === 'staff') && !isFull && !match.cancelled && (
              <Button
                onClick={() => setAdminAddModal(match)}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                data-testid={`admin-add-player-${match.id}`}
              >
                <Plus size={16} />
                {language === 'de' ? 'Spieler hinzufügen' : 'Add player'}
              </Button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in pb-28 md:pb-6" data-testid="open-matches">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            {t('openMatches')}
          </h1>
          <p className="text-slate-500">
            {language === 'de' 
              ? 'Finden Sie Spielpartner auf Ihrem Level'
              : 'Find playing partners at your level'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {(user?.role === 'admin' || user?.role === 'staff') && (
            <button
              type="button"
              onClick={() => setShowPast(p => !p)}
              data-testid="toggle-past-matches"
              className={`px-3 py-2 rounded-lg text-xs font-bold border transition ${
                showPast ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
            >
              {showPast
                ? (language === 'de' ? '✓ Vergangene sichtbar' : '✓ Showing past')
                : (language === 'de' ? 'Vergangene anzeigen' : 'Show past')}
            </button>
          )}
          <Button
            onClick={openCreateModal}
            className="btn-primary flex items-center gap-2"
            data-testid="create-match-btn"
          >
            <Plus size={18} weight="bold" />
            {t('createMatch')}
          </Button>
        </div>
      </div>

      {/* Player Level Info */}
      <div className="flex items-center gap-4 bg-white border border-slate-200 px-4 py-3 rounded-lg">
        <Medal size={24} className="text-blue-600" weight="duotone" />
        <div>
          <p className="text-sm text-slate-500">{t('playerLevel')}</p>
          <p className={`text-xl font-bold inline-flex items-center rounded-lg px-2 py-0.5 ${getLevelBadge(user.player_level)}`}>{user.player_level}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border border-slate-200 w-full grid grid-cols-2">
          <TabsTrigger 
            value="all" data-testid="matches-tab-all"
            className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900"
          >
            {language === 'de' ? 'Alle Spiele' : 'All Matches'}
          </TabsTrigger>
          <TabsTrigger 
            value="my" data-testid="matches-tab-my"
            className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-900"
          >
            {t('myMatches')} ({myMatches.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-64 bg-slate-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12 card-static">
              <Users size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
              <p className="text-slate-500 mb-4">
                {language === 'de' ? 'Keine offenen Spiele verfügbar' : 'No open matches available'}
              </p>
              <Button onClick={openCreateModal} className="btn-primary">
                {t('createMatch')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {matches.map(renderMatchCard)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="my" className="mt-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2].map(i => (
                <div key={i} className="h-64 bg-slate-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : myMatches.length === 0 ? (
            <div className="text-center py-12 card-static">
              <Users size={48} className="text-slate-500 mx-auto mb-4" weight="duotone" />
              <p className="text-slate-500">
                {language === 'de' ? 'Sie haben noch keine Spiele' : "You haven't joined any matches"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myMatches.map(renderMatchCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Match Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[calc(100vh-9rem)] overflow-y-auto top-[2%] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]" data-testid="create-match-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{t('createMatch')}</DialogTitle>
            <DialogDescription className="text-slate-500">
              {language === 'de' 
                ? 'Erstellen Sie ein offenes Spiel'
                : 'Create an open match for others to join'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            {/* Date */}
            <div className="space-y-2">
              <Label className="label-text">{t('selectDate')} *</Label>
              <Calendar
                mode="single"
                selected={newMatch.date}
                onSelect={(date) => date && setNewMatch({...newMatch, date})}
                locale={dateLocale}
                className="rounded-lg bg-slate-50 border border-slate-200"
                disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
              />
            </div>

            {/* Match Type & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-text">{t('matchType')}</Label>
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-900 text-sm flex items-center gap-2">
                  <Users size={14} weight="duotone" />
                  <span>{language === 'de' ? 'Doppel · 4 Spieler' : 'Doubles · 4 players'}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="label-text">{t('duration')}</Label>
                <Select value={newMatch.duration_minutes.toString()} onValueChange={(v) => setNewMatch({...newMatch, duration_minutes: parseInt(v)})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900" data-testid="match-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 z-[100]">
                    <SelectItem value="60">60 min</SelectItem>
                    <SelectItem value="90">90 min</SelectItem>
                    <SelectItem value="120">120 min</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Time — dynamic, only shows slots where ≥1 court is free */}
            <div className="space-y-2">
              <Label className="label-text">{t('selectTime')} *</Label>
              <p className="text-xs text-slate-500" data-testid="auto-court-hint">
                {language === 'de'
                  ? 'Wähle eine Zeit — wir suchen automatisch den Platz.'
                  : 'Pick a time — we\'ll find the court for you.'}
              </p>
              {loadingTimes ? (
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5 text-sm text-slate-400 italic" data-testid="match-times-loading">
                  {language === 'de' ? 'Verfügbare Zeiten werden geladen…' : 'Loading available times…'}
                </div>
              ) : availableTimes.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-sm text-amber-700" data-testid="match-times-empty">
                  {language === 'de'
                    ? 'Keine Plätze verfügbar an diesem Tag für diese Dauer. Bitte wähle ein anderes Datum oder eine andere Dauer.'
                    : 'No courts available on this day for this duration. Please pick another date or duration.'}
                </div>
              ) : (
                <Select value={newMatch.start_time} onValueChange={(v) => setNewMatch({...newMatch, start_time: v})}>
                  <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900" data-testid="match-time-select">
                    <SelectValue placeholder={language === 'de' ? 'Zeit wählen' : 'Pick a time'} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 z-[100] max-h-64">
                    {availableTimes.map(t => (
                      <SelectItem key={t.start_time} value={t.start_time}>
                        <span className="font-medium">{t.start_time}</span>
                        <span className="text-slate-400 ml-2 text-xs">
                          · {t.available_courts} {language === 'de'
                            ? (t.available_courts === 1 ? 'Platz frei' : 'Plätze frei')
                            : (t.available_courts === 1 ? 'court free' : 'courts free')}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Match Kind: Social vs Competitive */}
            <div className="space-y-2" data-testid="match-kind-section">
              <Label className="label-text">{language === 'de' ? 'Match-Typ' : 'Match type'}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setNewMatch({ ...newMatch, match_kind: 'social' })}
                  data-testid="match-kind-social"
                  className={`px-3 py-3 rounded-xl border text-left transition-all ${
                    newMatch.match_kind === 'social'
                      ? 'border-emerald-500 bg-emerald-50/60 ring-2 ring-emerald-300'
                      : 'border-slate-200 bg-white hover:border-emerald-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-base">🎾</span>
                    <span className="text-sm font-bold text-slate-900">{language === 'de' ? 'Social' : 'Social'}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-tight">
                    {language === 'de'
                      ? 'Locker spielen, kein Rating-Update'
                      : 'Just play for fun, no rating change'}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setNewMatch({ ...newMatch, match_kind: 'competitive' })}
                  data-testid="match-kind-competitive"
                  className={`px-3 py-3 rounded-xl border text-left transition-all ${
                    newMatch.match_kind === 'competitive'
                      ? 'border-amber-500 bg-amber-50/60 ring-2 ring-amber-300'
                      : 'border-slate-200 bg-white hover:border-amber-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-base">🏆</span>
                    <span className="text-sm font-bold text-slate-900">{language === 'de' ? 'Kompetitiv' : 'Competitive'}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-tight">
                    {language === 'de'
                      ? 'Sieger zählen — Padlr Rating wird aktualisiert'
                      : 'Result counts — your Padlr Rating updates'}
                  </p>
                </button>
              </div>
            </div>

            {/* Level Range */}
            <div>
              <p className="text-xs text-slate-500 mb-2" data-testid="level-suggestion-hint">
                {language === 'de'
                  ? `Vorgeschlagen basierend auf deinem Level (${Number(user?.player_level || 3.0)}). Du kannst es ändern.`
                  : `Suggested based on your level (${Number(user?.player_level || 3.0)}). You can change it.`}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-text">{language === 'de' ? 'Min Level' : 'Min Level'}</Label>
                  <Select value={newMatch.min_level.toString()} onValueChange={(v) => setNewMatch({...newMatch, min_level: parseFloat(v)})}>
                    <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900" data-testid="match-min-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7].map(l => (
                        <SelectItem key={l} value={l.toString()}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-text">{language === 'de' ? 'Max Level' : 'Max Level'}</Label>
                  <Select value={newMatch.max_level.toString()} onValueChange={(v) => setNewMatch({...newMatch, max_level: parseFloat(v)})}>
                    <SelectTrigger className="bg-slate-50 border-slate-200 text-slate-900" data-testid="match-max-level">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      {[1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7].map(l => (
                        <SelectItem key={l} value={l.toString()}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="label-text">{language === 'de' ? 'Notizen (optional)' : 'Notes (optional)'}</Label>
              <Input
                value={newMatch.notes}
                onChange={(e) => setNewMatch({...newMatch, notes: e.target.value})}
                className="bg-slate-50 border-slate-200 text-slate-900"
                placeholder={language === 'de' ? 'z.B. Anfängerfreundlich' : 'e.g. Beginner friendly'}
              />
            </div>

            {/* Payment method (creator only) */}
            <div className="space-y-2">
              <Label className="label-text">{language === 'de' ? 'Zahlung' : 'Payment'}</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setCreatePaymentMethod('club')}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${createPaymentMethod === 'club' ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                  data-testid="create-pay-club"
                >
                  {language === 'de' ? 'Im Club zahlen' : 'Pay at the club'}
                </button>
                <button
                  type="button"
                  onClick={() => setCreatePaymentMethod('online')}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${createPaymentMethod === 'online' ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                  data-testid="create-pay-online"
                >
                  {language === 'de' ? 'Online bezahlen' : 'Pay online'}
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateModal(false)}
                className="flex-1 btn-secondary"
              >
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleCreateMatch}
                disabled={!newMatch.start_time || availableTimes.length === 0}
                className="flex-1 btn-primary"
                data-testid="submit-create-match"
              >
                {t('createMatch')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Join Match — Payment Method picker */}
      <Dialog open={!!pendingJoinMatch} onOpenChange={(o) => !o && setPendingJoinMatch(null)}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm max-h-[calc(100vh-9rem)] overflow-y-auto top-[2%] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]" data-testid="join-payment-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {language === 'de' ? 'Wie möchtest du bezahlen?' : 'How would you like to pay?'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {pendingJoinMatch && (
                <>
                  {pendingJoinMatch.court_name} · {format(new Date(pendingJoinMatch.date), 'PPP', { locale: dateLocale })} · {pendingJoinMatch.start_time}
                  <br />
                  <span className="text-emerald-600 font-bold">
                    {formatPrice(pendingJoinMatch.cost_per_player)} CHF
                  </span>
                  {' '}{language === 'de' ? 'pro Spieler' : 'per player'}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 mt-2">
            <button
              type="button"
              onClick={() => confirmJoinMatch('club')}
              className="px-4 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-50 text-emerald-700 font-bold text-left flex items-center justify-between hover:bg-emerald-100 transition-colors"
              data-testid="join-pay-club"
            >
              <div>
                <div>{language === 'de' ? 'Im Club zahlen' : 'Pay at the club'}</div>
                <div className="text-xs font-normal text-emerald-600 mt-0.5">
                  {language === 'de' ? 'Bei Ankunft an der Rezeption' : 'At the front desk on arrival'}
                </div>
              </div>
              <Users size={22} weight="duotone" />
            </button>
            <button
              type="button"
              onClick={() => confirmJoinMatch('online')}
              className="px-4 py-3 rounded-xl border-2 border-slate-200 bg-white text-slate-700 font-bold text-left flex items-center justify-between hover:bg-slate-50 transition-colors"
              data-testid="join-pay-online"
            >
              <div>
                <div>{language === 'de' ? 'Online bezahlen' : 'Pay online'}</div>
                <div className="text-xs font-normal text-slate-500 mt-0.5">
                  {language === 'de' ? 'Sofort via Karte oder TWINT' : 'Instant via card or TWINT'}
                </div>
              </div>
              <CreditCard size={22} weight="duotone" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Submit Result Modal — competitive matches only */}
      <Dialog open={!!resultModal} onOpenChange={(o) => { if (!o) { setResultModal(null); setResultWinners([]); setCompResult({ valid: false }); } }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md max-h-[calc(100vh-9rem)] overflow-y-auto top-[2%] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]" data-testid="submit-result-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              🏆 {language === 'de' ? 'Sieger eingeben' : 'Submit result'}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {resultModal?.match_kind === 'competitive'
                ? (language === 'de'
                    ? 'Wertungsspiel — Teams wählen und Satzresultat eingeben. Padlr Rating wird automatisch aktualisiert.'
                    : 'Rated match — pick the teams and enter the set score. Padlr Rating updates automatically.')
                : (language === 'de'
                    ? 'Freundschaftsspiel — wähle das siegende Doppel. Kein Rating-Update, nur für die Erinnerung.'
                    : 'Friendly match — select the winning pair. No rating update, just for the record.')}
            </DialogDescription>
          </DialogHeader>
          {resultModal && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                <p className="font-bold text-slate-900">{resultModal.court_name}</p>
                <p className="text-slate-500">
                  {format(new Date(resultModal.date), 'PP', { locale: dateLocale })} · {resultModal.start_time}
                </p>
              </div>
              {resultModal.match_kind === 'competitive' ? (
                <CompetitiveResultForm players={resultModal.current_players} de={language === 'de'} onChange={setCompResult} />
              ) : (<>
              <div className="space-y-2">
                {resultModal.current_players.map(p => {
                  const sel = resultWinners.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => toggleWinner(p.id)}
                      data-testid={`result-player-${p.id}`}
                      className={`w-full px-3 py-2.5 rounded-lg border text-left flex items-center justify-between gap-2 transition ${
                        sel
                          ? 'border-amber-500 bg-amber-50/70 ring-2 ring-amber-300'
                          : 'border-slate-200 hover:border-amber-300 hover:bg-amber-50/30'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                          sel ? 'bg-amber-500 text-white border-amber-500' : 'bg-slate-100 text-slate-500 border-slate-300'
                        }`}>
                          {sel ? '✓' : ''}
                        </span>
                        <span className="font-medium text-slate-900 truncate">{p.name}</span>
                      </div>
                      <span className="text-[11px] text-slate-400">L{p.level?.toFixed(2) ?? '?'}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500">
                {language === 'de'
                  ? `Gewählt: ${resultWinners.length}/2 Sieger`
                  : `Selected: ${resultWinners.length}/2 winners`}
              </p>
              </>)}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => { setResultModal(null); setResultWinners([]); }} className="flex-1" data-testid="cancel-result-modal">
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleSubmitResult}
                  disabled={(resultModal.match_kind === 'competitive' ? !compResult.valid : resultWinners.length !== 2) || submittingResult}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-bold"
                  data-testid="confirm-result-submit"
                >
                  {submittingResult ? '…' : (language === 'de' ? 'Bestätigen' : 'Confirm')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin: add player to match */}
      <Dialog open={!!adminAddModal} onOpenChange={(o) => { if (!o) { setAdminAddModal(null); setAdminAddChosen(null); setAdminAddQuery(''); setAdminAddResults([]); } }}>
        <DialogContent className="bg-white border-purple-200 text-slate-900 max-w-md" data-testid="admin-add-player-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Plus size={20} weight="bold" className="text-purple-600" />
              {language === 'de' ? 'Spieler zum Match hinzufügen' : 'Add player to match'}
            </DialogTitle>
            <DialogDescription>
              {adminAddModal?.court_name} · {adminAddModal?.date} · {adminAddModal?.start_time}
              {adminAddModal ? <> · Level {adminAddModal.min_level}–{adminAddModal.max_level}</> : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-bold uppercase text-slate-500 tracking-wide">{language === 'de' ? 'Kunde suchen' : 'Search user'}</label>
              <Input
                value={adminAddQuery}
                onChange={(e) => { setAdminAddQuery(e.target.value); setAdminAddChosen(null); }}
                placeholder={language === 'de' ? 'Name, Email oder Telefon…' : 'Name, email or phone…'}
                className="bg-slate-50 border-slate-200"
                data-testid="admin-add-player-search"
              />
            </div>
            {adminAddQuery.length >= 2 && adminAddResults.length > 0 && !adminAddChosen && (
              <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-52 overflow-y-auto">
                {adminAddResults.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => setAdminAddChosen(u)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-purple-50 text-left"
                    data-testid={`admin-add-player-result-${u.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">{u.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                    </div>
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-slate-100 text-slate-600 whitespace-nowrap">
                      Level {(u.player_level ?? '?').toString()}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {adminAddChosen && (
              <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-900">{adminAddChosen.name}</div>
                  <div className="text-[11px] text-slate-500">{adminAddChosen.email} · Level {(adminAddChosen.player_level ?? '?').toString()}</div>
                </div>
                <button onClick={() => setAdminAddChosen(null)} className="text-xs text-purple-700 font-semibold hover:underline">
                  {language === 'de' ? 'Ändern' : 'Change'}
                </button>
              </div>
            )}
            <p className="text-[11px] text-slate-500 leading-snug">
              {language === 'de'
                ? 'Level-Check wird übersprungen. Der Spieler wird direkt eingebucht und benachrichtigt. Zahlung wird am Empfang abgewickelt (Klub-Zahlung).'
                : 'Level check is skipped. The player is added instantly and notified. Payment is handled at reception (club payment).'}
            </p>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setAdminAddModal(null); setAdminAddChosen(null); setAdminAddQuery(''); }} disabled={adminAdding} className="flex-1">
              {language === 'de' ? 'Abbrechen' : 'Cancel'}
            </Button>
            <Button
              onClick={submitAdminAddPlayer}
              disabled={!adminAddChosen || adminAdding}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold"
              data-testid="admin-add-player-confirm"
            >
              {adminAdding ? (language === 'de' ? 'Hinzufügen…' : 'Adding…') : (language === 'de' ? 'Zum Match hinzufügen' : 'Add to match')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenMatches;
